import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms and Conditions — Want To Buy" },
      { name: "description", content: "Want To Buy is a connection platform. These terms govern how buyers and sellers use the platform to find each other, and clarify that all transactions are arranged entirely between users." },
      { property: "og:title", content: "Terms and Conditions — Want To Buy" },
      { property: "og:description", content: "Want To Buy is a connection platform. These terms govern how buyers and sellers use the platform to find each other, and clarify that all transactions are arranged entirely between users." },
      { name: "twitter:title", content: "Terms and Conditions — Want To Buy" },
      { name: "twitter:description", content: "Want To Buy is a connection platform. These terms govern how buyers and sellers use the platform to find each other, and clarify that all transactions are arranged entirely between users." },
    ],
  }),
  component: TermsPage,
});

export function TermsContent() {
  return (
    <>
      <p className="!mt-0 text-xs uppercase tracking-wider text-muted-foreground">Last updated: July 2026</p>

      <section>
        <h2>1. What Want To Buy is</h2>
        <p>
          <strong>Want To Buy is a connection platform only.</strong> Its sole function is to connect TCG buyers with sellers who have the cards those buyers are looking for. Want To Buy is not a party to any transaction between users. Want To Buy does <strong>not</strong> process payments, hold funds, verify card authenticity, guarantee card condition, or mediate disputes.
        </p>
        <p>
          You must be at least 18 years old, provide accurate account information, and use the platform in good faith. You may not use the platform to harass other users, manipulate pricing, or post buy orders for items prohibited by law.
        </p>
      </section>

      <section>
        <h2>2. Buy orders, offers, and Purchase Confirmation</h2>
        <p>
          Buyers post buy orders describing the card they want and the price they're willing to pay. Each order is posted in one of two fulfillment modes chosen by the buyer:
        </p>
        <ul>
          <li>
            <strong>Offer-based orders.</strong> Sellers submit offers. When the buyer accepts an offer, the order enters a <strong>Purchase Confirmation</strong> step: the seller attests that they physically possess the card and can ship promptly, and the buyer has 24 hours to approve or decline that attestation. Only after the buyer approves the Purchase Confirmation does the seller's 48-hour window to submit the mandatory timestamped photos (see section 3) begin. If the buyer does not respond within 24 hours the confirmation expires and the order re-opens.
          </li>
          <li>
            <strong>Fixed-price ("Fulfill") orders.</strong> The buyer sets a firm price and any seller can accept it directly. Fixed-price orders <strong>skip the Purchase Confirmation step</strong>; the seller's 48-hour photo window begins immediately when they accept the order. The buyer's approval of the mandatory timestamped photos is the commitment checkpoint for this mode.
          </li>
        </ul>
        <p>
          In both modes the buyer and seller are only connected through the private message thread once the mandatory 2-photo requirement (section 3) has been completed and approved by the buyer.
        </p>
      </section>

      <section>
        <h2>3. The mandatory 2-photo scam filter</h2>
        <p>
          Before a buyer and seller are connected through the message thread, the seller must submit <strong>exactly 2 timestamped photos</strong>:
        </p>
        <ul>
          <li>Photo 1: the front of the card with a handwritten note clearly visible in the same frame.</li>
          <li>Photo 2: the back of the card with a handwritten note clearly visible in the same frame.</li>
        </ul>
        <p>The handwritten note in each photo must contain, written by hand:</p>
        <ul>
          <li>The current date in <strong>DD/MM/YYYY</strong> format.</li>
          <li><code>Seller:</code> followed by the seller's exact Want To Buy username.</li>
          <li><code>Buyer:</code> followed by the buyer's exact Want To Buy username.</li>
          <li>The full order ID.</li>
          <li>The card condition.</li>
        </ul>
        <p>
          For graded cards, one of the two photos must additionally show the slab label with the cert number legible alongside the handwritten note.
        </p>
        <p>
          <strong>This requirement exists solely as a scam-prevention filter.</strong> It proves that the seller physically possesses the card at the time of the transaction. Want To Buy does <strong>not</strong> authenticate, grade, or verify condition from these photos. The buyer is solely responsible for evaluating the photos and deciding whether to proceed. Sellers who cannot or will not submit these two photos cannot proceed.
        </p>
        <p>
          After reviewing the mandatory photos, the buyer may optionally request additional photos through the message thread. Sellers have <strong>24 hours</strong> to respond to additional photo requests.
        </p>
      </section>

      <section>
        <h2>4. Payments</h2>
        <p>
          <strong>Want To Buy does not process payments.</strong> Payment is arranged directly between the buyer and the seller once they are connected. Want To Buy strongly recommends <strong>PayPal Goods &amp; Services</strong> for every transaction, because PayPal provides independent buyer and seller protection through their own terms — protection that Want To Buy does not and cannot provide.
        </p>
        <p>
          See the <a href="/payment-safety">Payment Safety page</a> for a plain-language summary of what PayPal Buyer and Seller Protection cover.
        </p>
      </section>

      <section>
        <h2>5. Disputes</h2>
        <p>
          <strong>Want To Buy does not offer dispute resolution services.</strong> If a transaction goes wrong, buyers and sellers must resolve it directly through their payment provider. When PayPal Goods &amp; Services was used, disputes are handled through PayPal's Resolution Centre.
        </p>
        <p>
          Screenshots of the Want To Buy message thread — including the buy order, the submitted timestamped photos, agreed price and condition, and shipping communications — are accepted as evidence in PayPal Significantly Not as Described and Item Not Received disputes. Want To Buy will not provide transaction records to third parties except when legally required by court order or law enforcement request.
        </p>
      </section>

      <section>
        <h2>6. Privacy &amp; message monitoring</h2>
        <p>
          We collect only the information needed to operate the connection platform: account credentials, buy order records, message threads, and the mandatory timestamped photos. We do not sell user data to third parties. Public profiles display username, country (country-level only), TCG specialisations, ratings, and mutual reference counts.
        </p>
        <p className="panel-note">
          <strong>Admin access to private messages.</strong> Messages sent through Want To Buy are stored securely and may be accessed by administrators to investigate platform policy violations, detect fraud, and enforce platform rules (including the mandatory photo requirement). By creating an account you consent to this monitoring. Every admin who opens a thread generates an internal audit log entry.
        </p>
      </section>

      <section>
        <h2>7. Account termination</h2>
        <p>
          You may delete your account at any time from the <a href="/account">Account settings</a> page; deletion is reversible within 30 days, after which it becomes permanent. Want To Buy may suspend or permanently terminate accounts that violate these terms, the Seller Policy, or the Rating &amp; Strike Policy. Permanent bans for confirmed counterfeit sales or for circumventing a prior ban are final and not subject to appeal.
        </p>
      </section>

      <section>
        <h2>8. Liability disclaimer</h2>
        <p>
          <strong>Want To Buy is a connection platform only. Want To Buy is not a party to any transaction between users. Want To Buy does not process payments, hold funds, verify card authenticity, guarantee card condition, or mediate disputes.</strong>
        </p>
        <p>
          The mandatory 2-photo requirement is a scam-prevention filter only — Want To Buy makes no representations about the accuracy, authenticity, or condition of cards depicted in seller-submitted photos. All transactions are arranged entirely between buyers and sellers. <strong>Want To Buy accepts no liability whatsoever for the outcome of any transaction facilitated through connections made on the platform. Users transact entirely at their own risk.</strong>
        </p>
        <p>
          Want To Buy strongly recommends using <strong>PayPal Goods &amp; Services</strong> for all transactions, as it provides independent buyer and seller protection through PayPal's own terms and dispute resolution process.
        </p>
        <p>
          By using Want To Buy, users acknowledge and agree that the platform's sole function is to connect buyers and sellers and that all responsibility for transaction outcomes rests entirely with the transacting parties.
        </p>
      </section>

      <section>
        <h2>9. Trading (card-for-card swaps)</h2>
        <p>
          Card-for-card trades are outside the scope of the connection platform. Users who arrange trades through the messaging system do so entirely at their own risk. Want To Buy provides no protection or dispute channel for trades.
        </p>
      </section>

      <section>
        <h2>10. Changes to these terms</h2>
        <p>We may update these terms; material changes are communicated at least 30 days in advance via email and on-platform notification.</p>
      </section>

      <section>
        <h2>11. Governing law and jurisdiction</h2>
        <p>These terms are governed by the laws of the province of Quebec, Canada. Any disputes arising from use of the platform shall be subject to the jurisdiction of Quebec courts.</p>
      </section>
    </>
  );
}

function TermsPage() {
  return (
    <DocPage eyebrow="Legal" title="Terms and Conditions" intro="Want To Buy is a connection platform. These terms explain how the platform works and where our responsibility ends.">
      <TermsContent />
    </DocPage>
  );
}
